import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Utensils, Plus, Users, TrendingUp, TrendingDown, Clock, 
  ChevronRight, LogOut, Home, CreditCard, Menu, X,
  IndianRupee, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { getDashboard } from '../lib/api';
import { toast } from 'sonner';
import AnimatedBackground from '../components/AnimatedBackground';

const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
  GBP: '£',
  EUR: '€',
  AUD: 'A$',
  CAD: 'C$'
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await getDashboard();
      setDashboardData(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = CURRENCY_SYMBOLS[currency] || '₹';
    return `${symbol}${amount?.toFixed(2) || '0.00'}`;
  };

  const navItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard', active: true },
    { icon: Users, label: 'Groups', path: '/groups', active: false },
    { icon: CreditCard, label: 'Settlements', path: '/settlements', active: false },
  ];

  if (loading) {
    return (
      <AnimatedBackground>
        <div className="min-h-screen flex items-center justify-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
            <p className="text-slate-600 font-medium">Loading dashboard...</p>
          </motion.div>
        </div>
      </AnimatedBackground>
    );
  }

  return (
    <AnimatedBackground>
      <div className="min-h-screen flex">
      
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex w-72 flex-col fixed inset-y-0 left-0 glass-heavy border-r border-orange-100/50 z-40">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg">
              <Utensils className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-800 font-['Poppins']">BhojanSplit</span>
          </Link>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              className={`menu-item flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                item.active 
                  ? 'bg-orange-100 text-orange-600 active' 
                  : 'text-slate-600 hover:text-orange-600 hover:bg-orange-50'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        
        <div className="p-4 border-t border-orange-100">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-medium shadow-md">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-800 truncate">{user?.name}</p>
              <p className="text-sm text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full mt-2 flex items-center gap-3 px-4 py-3 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-xl border-b border-orange-100 z-40 flex items-center justify-between px-4 shadow-sm">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-md">
            <Utensils className="w-4 h-4 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-800 font-['Poppins']">BhojanSplit</span>
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 text-slate-700">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            className="lg:hidden fixed inset-0 z-50 bg-black/20 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          >
            <motion.aside
              onClick={(e) => e.stopPropagation()}
              className="w-72 h-full bg-white shadow-2xl"
            >
              <div className="p-6">
                <Link to="/" className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg">
                    <Utensils className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-2xl font-bold text-slate-800 font-['Poppins']">BhojanSplit</span>
                </Link>
              </div>
              <nav className="flex-1 px-4 py-6 space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`menu-item flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      item.active 
                        ? 'bg-orange-100 text-orange-600 active' 
                        : 'text-slate-600 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}
              </nav>
              <div className="p-4 border-t border-orange-100">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 lg:ml-72 pt-20 lg:pt-8 px-3 sm:px-4 lg:px-8 pb-24 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8"
          >
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Poppins'] mb-1 sm:mb-2 text-slate-800">
              Namaste, {user?.name?.split(' ')[0]}! 🙏
            </h1>
            <p className="text-sm sm:text-base text-slate-600">Here&apos;s your bhojan expense overview</p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="stat-card"
              data-testid="stat-you-owe"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <span className="text-sm sm:text-base text-slate-500">You Owe</span>
                <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <ArrowUpRight className="w-4 sm:w-5 h-4 sm:h-5 text-red-500" />
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-red-500">
                {formatCurrency(dashboardData?.balances?.total_owe)}
              </p>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {dashboardData?.balances?.you_owe?.length || 0} people
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="stat-card"
              data-testid="stat-you-are-owed"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <span className="text-sm sm:text-base text-slate-500">You&apos;re Owed</span>
                <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <ArrowDownRight className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-green-600">
                {formatCurrency(dashboardData?.balances?.total_owed)}
              </p>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {dashboardData?.balances?.you_are_owed?.length || 0} people
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="stat-card sm:col-span-2 md:col-span-1"
              data-testid="stat-monthly"
            >
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <span className="text-sm sm:text-base text-slate-500">This Month</span>
                <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <IndianRupee className="w-4 sm:w-5 h-4 sm:h-5 text-orange-600" />
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-800">
                {formatCurrency(dashboardData?.monthly_spending)}
              </p>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                Total spending
              </p>
            </motion.div>
          </div>

          {/* Balances & Recent Activity */}
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* People You Owe */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/90 backdrop-blur rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-orange-100 shadow-lg"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold font-['Poppins'] text-slate-800">People You Owe</h2>
                <TrendingUp className="w-5 h-5 text-red-500" />
              </div>
              {dashboardData?.balances?.you_owe?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.balances.you_owe.map((person, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-orange-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-medium shadow-md">
                          {person.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{person.name || person.email}</p>
                          <p className="text-sm text-slate-500">{person.email}</p>
                        </div>
                      </div>
                      <span className="text-red-500 font-semibold">{formatCurrency(person.amount, person.currency)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>You don&apos;t owe anyone!</p>
                </div>
              )}
            </motion.div>

            {/* People Who Owe You */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold font-['Poppins'] text-slate-800">People Who Owe You</h2>
                <TrendingDown className="w-5 h-5 text-green-600" />
              </div>
              {dashboardData?.balances?.you_are_owed?.length > 0 ? (
                <div className="space-y-4">
                  {dashboardData.balances.you_are_owed.map((person, index) => (
                    <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-green-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-medium shadow-md">
                          {person.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{person.name || person.email}</p>
                          <p className="text-sm text-slate-500">{person.email}</p>
                        </div>
                      </div>
                      <span className="text-green-600 font-semibold">{formatCurrency(person.amount, person.currency)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <TrendingDown className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No one owes you yet</p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Recent Expenses */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold font-['Poppins'] text-slate-800">Recent Expenses</h2>
              <Clock className="w-5 h-5 text-slate-400" />
            </div>
            {dashboardData?.recent_expenses?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recent_expenses.map((expense, index) => (
                  <Link
                    key={expense.expense_id}
                    to={`/expense/${expense.expense_id}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-orange-50 transition-all hover:translate-x-1"
                    data-testid={`expense-item-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                        <Utensils className="w-6 h-6 text-orange-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800">{expense.title}</p>
                          {expense.status === 'completed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-600">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              Completed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-600">
                              <Clock className="w-3 h-3" />
                              Pending
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {expense.merchant || expense.date} • {expense.participants?.length || 0} people
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-semibold text-slate-800">{formatCurrency(expense.total_amount, expense.currency)}</span>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Utensils className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg mb-2">No expenses yet</p>
                <p className="text-sm">Create your first split to get started!</p>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Floating Action Button */}
      <Link to="/split/new">
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.8, type: 'spring' }}
          className="fab"
          data-testid="create-split-fab"
        >
          <Plus className="w-7 h-7 text-white" />
        </motion.button>
      </Link>
    </div>
    </AnimatedBackground>
  );
};

export default Dashboard;
